// Home v2 — round quick-action buttons under the balance card (mobile tier).
// Public: solid accent circles with white glyphs. Private: soft accent circles
// with accent glyphs. Private-wallet actions are disabled (with the shared
// tooltip) for accounts that can't open a RAILGUN wallet, like the FAB menu.
import React from 'react'
import { Pressable, View } from 'react-native'

import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import { NavItem, QUICK_ACTIONS } from '../navConfig'
import { getModeAccent, getModeAccentSoft } from '../tokens'

const BUTTON_SIZE = 44

type Props = {
  mode: Mode
  privateModeReason: PrivateModeReason
  onPress: (item: NavItem) => void
}

const QuickActions: React.FC<Props> = ({ mode, privateModeReason, onPress }) => {
  const { theme } = useTheme()
  const isPrivate = mode === ModeEnum.PRIVATE
  const accent = getModeAccent(theme, mode)
  const actions = QUICK_ACTIONS[isPrivate ? 'private' : 'public']
  const privateBlocked = isPrivateModeBlocked(privateModeReason)
  const blockedTooltip =
    PRIVATE_MODE_TOOLTIP[privateModeReason as Exclude<PrivateModeReason, 'available'>]

  return (
    <View
      accessibilityRole="toolbar"
      accessibilityLabel="Wallet actions"
      style={{ flexDirection: 'row', justifyContent: 'center', paddingVertical: 4 }}
    >
      {actions.map((item) => {
        const Icon = item.icon
        const disabled = privateBlocked && !!item.privateAction
        const button = (
          <Pressable
            key={item.key}
            disabled={disabled}
            onPress={() => onPress(item)}
            accessibilityRole="button"
            accessibilityLabel={disabled ? `${item.label}. ${blockedTooltip}` : item.label}
            accessibilityState={{ disabled }}
            style={({ hovered }: any) => ({
              alignItems: 'center',
              gap: 8,
              opacity: disabled ? 0.5 : hovered ? 0.85 : 1
            })}
          >
            <View
              style={{
                width: BUTTON_SIZE,
                height: BUTTON_SIZE,
                borderRadius: BUTTON_SIZE / 2,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isPrivate ? getModeAccentSoft(theme, mode) : accent
              }}
            >
              <Icon width={20} height={20} color={isPrivate ? accent : '#fff'} />
            </View>
            <Text fontSize={12} weight="medium" appearance="primaryText" numberOfLines={1}>
              {item.label}
            </Text>
          </Pressable>
        )

        return (
          <View key={item.key} style={{ flex: 1, maxWidth: 96, alignItems: 'center' }}>
            {disabled ? (
              <View
                // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
                dataSet={createGlobalTooltipDataSet({
                  id: `quick-${item.key}-disabled`,
                  content: blockedTooltip
                })}
              >
                {button}
              </View>
            ) : (
              button
            )}
          </View>
        )
      })}
    </View>
  )
}

export default React.memo(QuickActions)
